/**
 * Market Neutral R — queued orders for tonight's close, a dry-run of today's orders, and the fill log.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { API } from "./stats/statsApi";

const ACTION_CLS = {
  BUY: "text-emerald-400", "BUY TO COVER": "text-emerald-400",
  SELL: "text-red-400", "SELL SHORT": "text-red-400",
};
const REASONS = {
  initial: "initial build", formation: "monthly formation",
  covers: "institutional short covers",
};
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const OrdersTable = ({ rows, testId, fills = false }) => (
  <div className="overflow-x-auto max-h-80 overflow-y-auto">
    <table className="w-full text-xs" data-testid={testId}>
      <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
        <tr>
          {fills && <th className="py-1.5 px-2 text-left">Date</th>}
          <th className="py-1.5 px-2 text-left">Action</th>
          <th className="py-1.5 px-2 text-left">Ticker</th>
          <th className="py-1.5 px-2 text-right">Shares</th>
          <th className="py-1.5 px-2 text-right">{fills ? "Fill (close)" : "Est. price"}</th>
          <th className="py-1.5 px-2 text-right">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o, i) => (
          <tr key={`${o.date || ""}-${o.symbol}-${i}`} className="border-b border-border/40"
              data-testid={`${testId}-row-${i}`}>
            {fills && <td className="py-1.5 px-2 font-mono text-muted-foreground">{o.date}</td>}
            <td className={`py-1.5 px-2 font-semibold ${ACTION_CLS[o.action] || ""}`}>{o.action}</td>
            <td className="py-1.5 px-2 font-semibold">{o.symbol}</td>
            <td className="py-1.5 px-2 text-right font-mono">{o.shares}</td>
            <td className="py-1.5 px-2 text-right font-mono">{money(fills ? o.px : o.est_px)}</td>
            <td className="py-1.5 px-2 text-right font-mono">{money(fills ? o.usd : o.est_usd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const reasonText = (rs) => (rs || []).map((r) => REASONS[r] || r).join(" · ");

const PreviewResult = ({ res }) => {
  if (res.status === "no_event") {
    return (
      <p className="text-xs text-muted-foreground" data-testid="mnr-preview-none">
        No institutional trade event today (no formation or cover) — no orders,
        no email.
      </p>
    );
  }
  if (res.status !== "ok") {
    return (
      <p className="text-xs text-red-400" data-testid="mnr-preview-error">
        {res.error || res.detail || res.status || "Preview failed"}
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="mnr-preview-result">
      <p className="text-xs text-muted-foreground">
        {res.n_orders} order(s) · {reasonText(res.reasons)} · book {money(res.nav_est)} ·
        {" "}{res.n_target} positions after trades
      </p>
      {res.note && <p className="text-xs text-amber-300" data-testid="mnr-preview-note">{res.note}</p>}
      {res.n_orders > 0 ? <OrdersTable rows={res.orders} testId="mnr-preview-orders" /> : (
        <p className="text-xs text-muted-foreground" data-testid="mnr-preview-zero">
          Event today, but the whole-share book already matches the target — no orders, no email.
        </p>
      )}
    </div>
  );
};

export const MnrActivity = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    fetch(`${API}/live/mn-r/activity`, { headers })
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (token) load(); }, [token, load]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/live/mn-r/orders/preview`, { headers });
      setPreview(await r.json());
    } catch (e) {
      setPreview({ status: "error", error: String(e.message || e) });
    } finally { setBusy(false); }
  };

  const pending = data?.account?.pending;
  const trades = data?.trades || [];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="mnr-activity">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">
            Tonight's orders <span className="text-muted-foreground font-normal">
              (emailed 18:00 UK on order days, filled at the close)</span>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={runPreview} disabled={busy}
                  data-testid="mnr-preview-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            Preview today
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {pending ? (
            <div className="space-y-2" data-testid="mnr-pending">
              <p className="text-xs text-amber-300">
                Queued {pending.date} · {pending.orders.length} order(s) · {reasonText(pending.reasons)}
              </p>
              <OrdersTable rows={pending.orders} testId="mnr-pending-orders" />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="mnr-no-pending">
              Nothing queued for tonight's close.
            </p>
          )}
          {preview && <PreviewResult res={preview} />}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Fill log <span className="text-muted-foreground font-normal">
              (whole shares at closing prices, newest first)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trades.length ? <OrdersTable rows={trades} testId="mnr-fills" fills /> : (
            <p className="text-xs text-muted-foreground" data-testid="mnr-no-fills">
              No live fills yet — the first order day builds the full book from cash.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
